import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  Bars3Icon, 
  XMarkIcon, 
  UserIcon,
  ChevronDownIcon,
  MagnifyingGlassIcon,
  GlobeAltIcon,
  ChartBarIcon,
  SparklesIcon,
  UserGroupIcon,
  LinkIcon,
  ClipboardDocumentListIcon,
  DocumentChartBarIcon
} from '@heroicons/react/24/outline';

const Navbar = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated, logout } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isToolsMenuOpen, setIsToolsMenuOpen] = useState(false);
  const toolsMenuRef = useRef(null);
  const userMenuRef = useRef(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (toolsMenuRef.current && !toolsMenuRef.current.contains(event.target)) {
        setIsToolsMenuOpen(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    await logout();
    navigate('/');
    setIsUserMenuOpen(false);
  };

  return (
    <nav className="bg-white shadow-lg relative z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo and primary nav */}
          <div className="flex items-center">
            <Link to="/" className="flex-shrink-0 flex items-center">
              <img src="/images/serpfinity-logo.png" alt="SERPfinity" className="h-10" />
            </Link>
            
            {/* Desktop navigation */}
            <div className="hidden md:ml-8 md:flex md:space-x-8">
              <Link
                to="/"
                className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors"
              >
                Home
              </Link>
              {/* Tools & Reports Dropdown */}
              <div className="relative" ref={toolsMenuRef}>
                <button
                  onClick={() => setIsToolsMenuOpen(!isToolsMenuOpen)}
                  className="flex items-center text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors"
                >
                  Tools & Reports
                  <ChevronDownIcon className="ml-1 h-4 w-4" />
                </button>
                
                {isToolsMenuOpen && (
                  <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5">
                    <Link
                      to="/keyword-research"
                      onClick={() => setIsToolsMenuOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <MagnifyingGlassIcon className="h-4 w-4 mr-3 text-gray-400" />
                      Keyword Research
                    </Link>
                    <Link
                      to="/domain-analysis"
                      onClick={() => setIsToolsMenuOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <GlobeAltIcon className="h-4 w-4 mr-3 text-gray-400" />
                      Domain Analysis
                    </Link>
                    <Link
                      to="/rank-tracking"
                      onClick={() => setIsToolsMenuOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <ChartBarIcon className="h-4 w-4 mr-3 text-gray-400" />
                      Rank Tracking
                    </Link>
                    <Link
                      to="/content-writer"
                      onClick={() => setIsToolsMenuOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <SparklesIcon className="h-4 w-4 mr-3 text-gray-400" />
                      AI Content Writer
                    </Link>
                    <div className="border-t border-gray-100 my-1"></div>
                    <Link
                      to="/competitive-analysis"
                      onClick={() => setIsToolsMenuOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <UserGroupIcon className="h-4 w-4 mr-3 text-gray-400" />
                      Competitive Analysis
                    </Link>
                    <Link
                      to="/backlink-explorer"
                      onClick={() => setIsToolsMenuOpen(false)}
                      className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      <LinkIcon className="h-4 w-4 mr-3 text-gray-400" />
                      Backlink Explorer
                    </Link>
              <div className="border-t border-gray-100 my-1"></div>
              <p className="px-4 py-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">Coming Soon</p>
              <Link
                to="/projects"
                onClick={() => setIsToolsMenuOpen(false)}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <ClipboardDocumentListIcon className="h-4 w-4 mr-3 text-gray-400" />
                Projects
                <span className="ml-auto px-1.5 py-0.5 text-[9px] font-bold bg-orange-500 text-white rounded-full">SOON</span>
              </Link>
              <Link
                to="/my-reports"
                onClick={() => setIsToolsMenuOpen(false)}
                className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
              >
                <DocumentChartBarIcon className="h-4 w-4 mr-3 text-gray-400" />
                My Reports
                <span className="ml-auto px-1.5 py-0.5 text-[9px] font-bold bg-orange-500 text-white rounded-full">SOON</span>
              </Link>
                  </div>
                )}
              </div>
              
              <Link
                to="/pricing"
                className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors"
              >
                Pricing
              </Link>
              {isAuthenticated && (
                <Link
                  to="/dashboard"
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors"
                >
                  Dashboard
                </Link>
              )}
            </div>
          </div>

          {/* Desktop auth buttons */}
          <div className="hidden md:flex md:items-center md:space-x-4">
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors"
                >
                  <UserIcon className="h-5 w-5 mr-2" />
                  {user?.firstName || user?.email}
                </button>
                
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 ring-1 ring-black ring-opacity-5">
                    <Link
                      to="/profile"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Profile
                    </Link>
                    <Link
                      to="/dashboard"
                      onClick={() => setIsUserMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Dashboard
                    </Link>
                    <div className="border-t border-gray-100"></div>
                    <button
                      onClick={handleLogout}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Sign out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="text-gray-700 hover:text-blue-600 px-3 py-2 text-sm font-medium transition-colors"
                >
                  Sign in
                </Link>
                <Link
                  to="/register"
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="text-gray-700 hover:text-blue-600 p-2"
                aria-label={isMobileMenuOpen ? "Close navigation menu" : "Open navigation menu"}
                aria-expanded={isMobileMenuOpen}
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-gray-50 rounded-md mt-2">
              <Link
                to="/"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-blue-600 px-3 py-2 text-base font-medium"
              >
                Home
              </Link>
              <div className="border-t border-gray-200 mt-2 pt-2">
                <p className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wide">Tools & Reports</p>
                <Link
                  to="/keyword-research"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-gray-700 hover:text-blue-600 px-3 py-2 text-base font-medium"
                >
                  Keyword Research
                </Link>
                <Link
                  to="/domain-analysis"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-gray-700 hover:text-blue-600 px-3 py-2 text-base font-medium"
                >
                  Domain Analysis
                </Link>
                <Link
                  to="/rank-tracking"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-gray-700 hover:text-blue-600 px-3 py-2 text-base font-medium"
                >
                  Rank Tracking
                </Link>
                <Link
                  to="/content-writer"
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block text-gray-700 hover:text-blue-600 px-3 py-2 text-base font-medium"
                >
                  AI Content Writer
                </Link>
              <Link
                to="/projects"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-blue-600 px-3 py-2 text-base font-medium"
              >
                Projects <span className="px-1.5 py-0.5 text-[9px] font-bold bg-orange-500 text-white rounded-full">SOON</span>
              </Link>
              <Link
                to="/my-reports"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-blue-600 px-3 py-2 text-base font-medium"
              >
                My Reports <span className="px-1.5 py-0.5 text-[9px] font-bold bg-orange-500 text-white rounded-full">SOON</span>
              </Link>
              </div>
              <Link
                to="/pricing"
                onClick={() => setIsMobileMenuOpen(false)}
                className="block text-gray-700 hover:text-blue-600 px-3 py-2 text-base font-medium"
              >
                Pricing
              </Link>
              {isAuthenticated && (
                <>
                  <Link
                    to="/dashboard"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block text-gray-700 hover:text-blue-600 px-3 py-2 text-base font-medium"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/profile"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block text-gray-700 hover:text-blue-600 px-3 py-2 text-base font-medium"
                  >
                    Profile
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="block w-full text-left text-gray-700 hover:text-blue-600 px-3 py-2 text-base font-medium"
                  >
                    Sign out
                  </button>
                </>
              )}
              {!isAuthenticated && (
                <>
                  <Link
                    to="/login"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block text-gray-700 hover:text-blue-600 px-3 py-2 text-base font-medium"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    onClick={() => setIsMobileMenuOpen(false)}
                    className="block bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-md text-base font-medium mx-3 text-center"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navbar;